import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Tbody, Td, Th, Thead, Tr } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { bandasAPI } from '../services/api';

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function BandaRepertorioShareIncidencias({ active }) {
  
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState(''); // '' | PENDIENTE | RESUELTA
  const [patchingId, setPatchingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await bandasAPI.listRepertorioShareIncidencias();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toaster.create({
        title: 'No se pudieron cargar las incidencias',
        type: 'error',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const filtered = useMemo(() => {
    if (!filtroEstado) return rows;
    return rows.filter((r) => r.estado === filtroEstado);
  }, [rows, filtroEstado]);

  const toggleEstado = async (row) => {
    const next = row.estado === 'RESUELTA' ? 'PENDIENTE' : 'RESUELTA';
    setPatchingId(row.id);
    try {
      await bandasAPI.patchRepertorioShareIncidencia(row.id, { estado: next });
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                estado: next,
                resueltaAt: next === 'RESUELTA' ? new Date().toISOString() : null,
              }
            : r,
        ),
      );
      toaster.create({
        title: next === 'RESUELTA' ? 'Marcada como resuelta' : 'Marcada como pendiente',
        type: 'success',
        duration: 2500,
      });
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo actualizar',
        description: Array.isArray(msg) ? msg.join('. ') : msg || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setPatchingId(null);
    }
  };

  return (
    <VStack align="stretch" gap={4} pt={2}>
      <HStack flexWrap="wrap" justify="space-between" gap={3}>
        <Text fontSize="sm" color="gray.600">
          Incidencias reportadas desde el enlace público, ordenadas por fecha (más recientes primero).
        </Text>
        <HStack gap={2}>
          <NativeSelect.Root>
            <NativeSelect.Field
              size="sm"
              maxW="200px"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="">Todas</option>
              <option value="PENDIENTE">Pendientes</option>
              <option value="RESUELTA">Resueltas</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Button size="sm" variant="outline" onClick={() => void load()} loading={loading}>
            Actualizar
          </Button>
        </HStack>
      </HStack>

      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner color="purple.500" />
        </Flex>
      ) : filtered.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          {filtroEstado ? 'No hay incidencias con ese filtro.' : 'Aún no hay incidencias reportadas.'}
        </Text>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderColor={borderCol} borderRadius="md">
          <Table.Root size="sm" variant="simple">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader whiteSpace="nowrap">Fecha</Table.ColumnHeader>
                <Table.ColumnHeader>Estado</Table.ColumnHeader>
                <Table.ColumnHeader>Carpeta / obra</Table.ColumnHeader>
                <Table.ColumnHeader>Instrumento / voz</Table.ColumnHeader>
                <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                <Table.ColumnHeader>Músico</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">Acción</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filtered.map((r) => (
                <Table.Row key={r.id} opacity={r.estado === 'RESUELTA' ? 0.75 : 1}>
                  <Table.Cell whiteSpace="nowrap" fontSize="xs" color="gray.600">
                    {formatFecha(r.createdAt)}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={r.estado === 'RESUELTA' ? 'green' : 'orange'} fontSize="0.65rem">
                      {r.estado === 'RESUELTA' ? 'Resuelta' : 'Pendiente'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell fontSize="sm" maxW="220px">
                    <Text fontWeight="600" lineClamp={1}>
                      {r.carpetaNombre}
                    </Text>
                    {r.obraTitulo ? (
                      <Text fontSize="xs" color="gray.600" lineClamp={2}>
                        {r.obraTitulo}
                      </Text>
                    ) : null}
                    {r.descripcion ? (
                      <Text fontSize="xs" color="gray.500" mt={1} lineClamp={3} title={r.descripcion}>
                        {r.descripcion}
                      </Text>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell fontSize="xs" color="gray.600" maxW="180px">
                    <Text lineClamp={2}>{r.instrumentoNombre || r.instrumentoSlug || '—'}</Text>
                    {r.vozEtiquetasDisplay ? (
                      <Text lineClamp={1} mt={0.5}>
                        Voz: {r.vozEtiquetasDisplay}
                      </Text>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell fontSize="sm">{r.tipoLabel || r.tipo}</Table.Cell>
                  <Table.Cell fontSize="sm" color="gray.600">
                    {r.musicoNombre || '—'}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button
                      size="xs"
                      variant="outline"
                      colorPalette={r.estado === 'RESUELTA' ? 'orange' : 'green'}
                      onClick={() => void toggleEstado(r)}
                      loading={patchingId === r.id}
                    >
                      {r.estado === 'RESUELTA' ? 'Marcar pendiente' : 'Marcar resuelta'}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );
}
